import { writable } from "svelte/store";
import { callQueue } from "../storage.js";
import { optionOperation } from '../../cicero-fronto/utility.js'
import { squareDist, deepcopy } from "../utility.js";
export const AugmentationStack = writable({});

export function getAugmentation(role, org_rule, oldValue, newValue, spec, scope, detail) {
  let isMobile = spec?.artboard?.screen_type?.startsWith("Phone-");
  let isPrint = spec?.artboard?.screen_type?.startsWith("Print");
  let isWatch = spec?.artboard?.screen_type?.startsWith("Watch");
  let isThumb = spec?.artboard?.screen_type?.startsWith("Thumb");
  let isLarge = spec?.artboard?.screen_type?.startsWith("Desktop-") || spec?.artboard?.screen_type?.startsWith("Tablet-");
  let isTiny = isWatch || isThumb;
  let augment_rules = [];
  // fix tooltip
  if (isMobile && role == "tooltip" && newValue === "new") {
    let rule = {
      category: "augment",
      description: "Fix tooltip",
      specifier: {
        role: "tooltip",
      },
      action: "modify",
      option: {
        external: true,
        fixed: true,
        position: "bottom"
      }
    };
    if (org_rule.specifier.id) rule.specifier.layer = { id: org_rule.specifier.id };
    else if (org_rule.specifier.index !== undefined) rule.specifier.layer = { index: org_rule.specifier.index };
    augment_rules.push({
      name: "Fix tooltip?",
      rule,
      scope
    })
  } else if (role === "annotation.external" && !oldValue && newValue) {
    if (!detail.visibility?.externalize?.numbering) {
      let rule1 = {
        category: "augment",
        description: "Number annotation",
        specifier: org_rule.specifier,
        action: "modify",
        option: {
          numbering: true
        }
      };
      augment_rules.push({
        name: "Number annotation?",
        rule: rule1,
        scope
      });
    }
    if (detail.text?.every((t) => t.align !== "left")) {
      let rule2 = {
        category: "augment",
        description: "Align left",
        specifier: org_rule.specifier,
        action: "modify",
        option: {
          textAlign: "left"
        }
      };
      augment_rules.push({
        name: "Align left?",
        rule: rule2,
        scope
      });
    }
  } else if (["annotation.dx", "annotation.dy"].includes(role) && newValue !== undefined && !detail.anchor) {
    if (detail.anchor?.callout !== "yes") {
      if (oldValue === undefined || Math.abs(parseFloat(oldValue)) < Math.abs(parseFloat(newValue))) {
        let rule = {
          category: "augment",
          description: "Add tick",
          specifier: org_rule.specifier,
          action: "modify",
          option: {
            tick: true
          }
        };
        augment_rules.push({
          name: "Add tick?",
          rule: rule,
          scope
        });
      }
    } else if (detail.anchor?.callout !== "no" || detail.anchor?.callout === undefined) {
      if (oldValue !== undefined && Math.abs(parseFloat(oldValue)) > Math.abs(parseFloat(newValue))) {
        let rule = {
          category: "augment",
          description: "Remove tick",
          specifier: org_rule.specifier,
          action: "modify",
          option: {
            tick: null
          }
        };
        augment_rules.push({
          name: "Remove tick?",
          rule: rule,
          scope
        });
      }
    }
  } else if (role === "annotation.position" && newValue !== undefined) {
    if (detail && spec?.fronto) {
      let ref1 = spec?.fronto?.layout?.width;
      let ref2 = spec?.fronto?.layout?.height;
      let newdx = optionOperation(oldValue[0], newValue[0], ref1);
      let newdy = optionOperation(oldValue[1], newValue[1], ref2);
      let is_farther = squareDist(oldValue[0], oldValue[1]) < squareDist(newdx, newdy)
      if (detail?.anchor?.callout !== "yes") {
        if (is_farther) {
          let rule = {
            category: "augment",
            description: "Add tick",
            specifier: org_rule.specifier,
            action: "modify",
            option: {
              tick: true
            }
          };
          augment_rules.push({
            name: "Add tick?",
            rule: rule,
            scope
          });
        }
      } else if (detail.anchor?.callout !== "no" || detail.anchor?.callout === undefined) {
        if (!is_farther) {
          let rule = {
            category: "augment",
            description: "Remove tick",
            specifier: org_rule.specifier,
            action: "modify",
            option: {
              tick: null
            }
          };
          augment_rules.push({
            name: "Remove tick?",
            rule: rule,
            scope
          });
        }
      }
    }
  } else if (isLarge && role === "title" && org_rule.action === "remove") {
    let leftovers = spec.fronto?.title?.items?.filter((d) => d.name !== detail.name);
    if (leftovers?.length == 1) {
      let rule = {
        category: "augment",
        description: "Internalize title",
        specifier: {
          role: "title"
        },
        action: "replace",
        option: {
          to: {
            role: "annotation",
            internal: true,
            fontSize: 14,
            x: "50%",
            y: "50%",
            dx: "-50%",
            dy: "-50%",
            width: spec.fronto?.layout?.width || 150
          },
          separate: false
        }
      };
      if (leftovers[0].name) rule.specifier.id = leftovers[0].name;
      else rule.specifier.index = 0;
      augment_rules.push({
        name: "Internalize title?",
        rule,
        scope
      });
    }
  } else if (["artboard_width", "artboard_height"].includes(role)) {
    if (oldValue?.constructor.name === "Array" && newValue?.constructor.name === "Array"
      && oldValue.length == 2 && newValue.length == 2
      && oldValue.every(d => d !== undefined) && newValue.every(d => d !== undefined)) {
      let is_smaller = oldValue[0] * oldValue[1] > newValue[0] * newValue[1];
      let is_map = spec?.fronto?.layout?.composition === "projection";
      if (is_smaller && is_map) {
        let rule = {
          category: "augment",
          description: "Add zoom + pan",
          specifier: {
            role: "interaction"
          },
          action: "add",
          option: {
            interaction: "zoompan"
          }
        };
        augment_rules.push({
          name: "Add zoom?",
          rule,
          scope
        });
      }
      let size_factor = (newValue[0] * newValue[1]) / (oldValue[0] * oldValue[1]);
      if (!isNaN(size_factor)) {
        spec?.fronto?.layer?.forEach((layer, li) => {
          if (layer?.mark?.size !== undefined) {
            let rule = {
              category: "augment",
              description: "Rescale mark size",
              specifier: {
                role: "mark",
                mark: layer.mark.type
              },
              action: "modify",
            };
            if (layer.name) rule.specifier.id = layer.name;
            else rule.specifier.index = li;
            if (layer.mark.size.field !== undefined) {
              let old_scale = deepcopy(layer.mark.size.scale.range);
              let old_scale_max = Math.max(...old_scale), old_scale_min = Math.min(...old_scale);
              let new_scale_max = Math.min(old_scale_max * size_factor, 5000), new_scale_min = Math.max(old_scale_min * size_factor, 9);
              let new_scale = old_scale.map(d => {
                return new_scale_min + (d - old_scale_min) / (old_scale_max - old_scale_min) * (new_scale_max - new_scale_min);
              });
              rule.option = {
                size: {
                  scale: { range: new_scale }
                }
              }
            } else if (layer.mark.size.value !== undefined) {
              let new_value = Math.min(Math.max(layer.mark.size.value * size_factor, 9), 5000);
              rule.option = {
                size: { value: new_value }
              };
            }
            if (rule.option) {
              augment_rules.push({
                name: "Rescale mark size?",
                rule,
                scope
              });
            }
          }
        });
      }
    }
  }

  AugmentationStack.update((stack) => {
    stack[spec.uid] = augment_rules;
    return stack;
  });
}

export function emptyAugmentationStack(uid) {
  AugmentationStack.update((stack) => {
    stack[uid] = [];
    return stack;
  });
}

export function runAugmentation(uid, aug_item, index) {
  // after run => delete
  AugmentationStack.update((stack) => {
    if (stack[uid].constructor.name === "Array") {
      // run it
      callQueue.update((queue) => {
        queue.add([aug_item.rule], aug_item.scope).apply();
        return queue;
      });
      stack[uid].splice(index, 1);
    }
    return stack;
  });
}
export function deleteAugmentation(uid, index) {
  // after run => delete
  AugmentationStack.update((stack) => {
    if (stack[uid].constructor.name === "Array") {
      stack[uid].splice(index, 1);
    }
    return stack;
  });
}